/**
 * Macro #26 · Increment 2 — ENTER 3D affordance (Level 2 only, NO three).
 *
 * Instead of a pill button, each territory that has a VALID published 3D scene
 * shows a large, semi-transparent, softly-glowing "3D" watermark laid OVER its
 * 2D Canvas image. It invites the curious visitor to click it and tilt the
 * territory into 3D. Rendered ONLY inside the INTERMEDIATE band (the caller
 * passes a non-null intermediate camera). Pointer-transparent container so
 * pan/zoom pass through; only the watermark itself is interactive.
 */
import { memo } from "react";

import { useI18n } from "../i18n";
import { worldToScreen, type Camera } from "../viewer/camera";
import { unionBoundsWorld, type UnitRect } from "../viewer/brand-image-geometry";

export interface Enter3DItem {
  identityId: string;
  displayName: string | null;
  units: UnitRect[];
  voxelCount: number;
  /** World XY footprint of each published 3D work art — a glowing "3D" mark is
   * laid over EACH one. Empty ⇒ a single mark over the whole territory. */
  artworks?: Array<{ x: number; y: number; w: number; h: number }>;
}

const MIN_UNION_PX = 48;
const MIN_ARTWORK_PX = 30;

const GLOW_CSS = `
.enter3d-label {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.08em;
  color: rgba(201, 247, 255, 0.7);
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  text-shadow:
    0 0 12px rgba(34, 211, 238, 0.7),
    0 0 30px rgba(34, 211, 238, 0.45),
    0 2px 10px rgba(0, 0, 0, 0.6);
  transition: transform 200ms ease, color 200ms ease, text-shadow 200ms ease;
  animation: enter3dPulse 3.2s ease-in-out infinite;
}
.enter3d-label:hover,
.enter3d-label:focus-visible {
  outline: none;
  color: rgba(232, 253, 255, 0.96);
  transform: translate(-50%, -50%) scale(1.07);
  text-shadow:
    0 0 16px rgba(34, 211, 238, 0.95),
    0 0 44px rgba(34, 211, 238, 0.7),
    0 2px 12px rgba(0, 0, 0, 0.7);
  animation-play-state: paused;
}
@keyframes enter3dPulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.9; }
}
@media (prefers-reduced-motion: reduce) {
  .enter3d-label { animation: none; opacity: 0.75; }
}
`;

export const Enter3DAffordance = memo(function Enter3DAffordance({
  camera,
  items,
  onEnter,
}: {
  camera: Camera | null;
  items: Enter3DItem[];
  onEnter: (item: Enter3DItem) => void;
}) {
  const { t } = useI18n();
  if (!camera || items.length === 0) return null;
  return (
    <div
      data-testid="enter-3d-layer"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}
    >
      <style>{GLOW_CSS}</style>
      {items.map((it) => {
        // A glowing "3D" mark over EACH published work art (centred + scaled to
        // that artwork). If per-artwork rects are missing, fall back to one mark
        // over the whole territory footprint.
        const wb = unionBoundsWorld(it.units);
        const rects = (it.artworks && it.artworks.length > 0)
          ? it.artworks
          : (wb ? [{ x: wb.x, y: wb.y, w: wb.w, h: wb.h }] : []);
        const gateW = (it.artworks && it.artworks.length > 0) ? MIN_ARTWORK_PX : MIN_UNION_PX;
        return rects.map((r, i) => {
          const tl = worldToScreen(camera, r.x, r.y);
          const w = r.w * camera.scale;
          const h = r.h * camera.scale;
          if (Math.min(w, h) < gateW) return null;
          const cx = tl.x + w / 2;
          const cy = tl.y + h / 2;
          const fontSize = Math.max(20, Math.min(Math.min(w, h) * 0.42, 128));
          return (
            <button
              key={`${it.identityId}-${i}`}
              data-testid={i === 0 ? `enter-3d-btn-${it.identityId}` : `enter-3d-btn-${it.identityId}-${i}`}
              data-voxel-count={it.voxelCount}
              title={t("grid.enter3d.title")}
              aria-label={`${t("grid.enter3d.title")} · ${it.displayName || t("grid.enter3d.fallbackName")}`}
              className="enter3d-label"
              onClick={() => onEnter(it)}
              style={{
                position: "absolute",
                left: cx,
                top: cy,
                transform: "translate(-50%, -50%)",
                pointerEvents: "auto",
                fontSize,
              }}
            >
              3D
            </button>
          );
        });
      })}
    </div>
  );
});
